(function () {
  const SCROLL_SELECTOR = "[data-horizontal-scroll]";
  const MIN_THUMB_PX = 24;
  const HIDE_AFTER_MS = 900;
  const MOBILE_MQ = window.matchMedia("(max-width: 63.99em)");

  class HorizontalScrollbar extends HTMLElement {
    connectedCallback() {
      this._viewport = this.querySelector(SCROLL_SELECTOR);
      if (!this._viewport) return;

      this._hideTimer = null;
      this._dragging = false;

      this._track = document.createElement("div");
      this._track.className = "horizontal-scrollbar__track";
      this._track.setAttribute("role", "presentation");

      this._thumb = document.createElement("div");
      this._thumb.className = "horizontal-scrollbar__thumb";
      this._thumb.setAttribute("role", "slider");
      this._thumb.setAttribute("aria-orientation", "horizontal");
      this._thumb.tabIndex = -1;

      this._track.appendChild(this._thumb);
      this.appendChild(this._track);

      this._viewport.addEventListener("scroll", this._onScroll, { passive: true });
      this._track.addEventListener("pointerdown", this._onTrackPointerDown);
      this._thumb.addEventListener("pointerdown", this._onThumbPointerDown);

      this._onMobileChange = () => {
        if (!this._isMobile()) {
          this._hideIndicator(true);
        }
        this._update();
      };
      MOBILE_MQ.addEventListener("change", this._onMobileChange);

      this._resizeObserver = new ResizeObserver(() => this._update());
      this._resizeObserver.observe(this._viewport);
      this._resizeObserver.observe(this._track);

      this._mutationObserver = new MutationObserver(() => this._update());
      this._mutationObserver.observe(this._viewport, { childList: true, subtree: true });

      this._update();
    }

    disconnectedCallback() {
      this._viewport?.removeEventListener("scroll", this._onScroll);
      this._track?.removeEventListener("pointerdown", this._onTrackPointerDown);
      this._thumb?.removeEventListener("pointerdown", this._onThumbPointerDown);
      MOBILE_MQ.removeEventListener("change", this._onMobileChange);
      this._resizeObserver?.disconnect();
      this._mutationObserver?.disconnect();
      this._clearHideTimer();
      this._endDrag();
    }

    _isMobile() {
      return MOBILE_MQ.matches;
    }

    _onScroll = () => {
      this._positionThumb();
      if (!this._isMobile() || !this._isScrollable()) return;
      this._showIndicator();
      this._scheduleHide();
    };

    _isScrollable() {
      if (!this._viewport) return false;
      return this._viewport.scrollWidth > this._viewport.clientWidth + 1;
    }

    _update() {
      const scrollable = this._isScrollable();
      this.classList.toggle("is-scrollable", scrollable);
      if (!scrollable) {
        this._hideIndicator(true);
        return;
      }
      this._positionThumb();
    }

    _showIndicator() {
      this.classList.add("is-visible");
    }

    _hideIndicator(immediate) {
      this._clearHideTimer();
      if (immediate) {
        this.classList.remove("is-visible");
        return;
      }
      this.classList.remove("is-visible");
    }

    _scheduleHide() {
      if (this._dragging) return;
      this._clearHideTimer();
      this._hideTimer = window.setTimeout(() => {
        this._hideTimer = null;
        if (!this._dragging) {
          this._hideIndicator(true);
        }
      }, HIDE_AFTER_MS);
    }

    _clearHideTimer() {
      if (this._hideTimer != null) {
        window.clearTimeout(this._hideTimer);
        this._hideTimer = null;
      }
    }

    _positionThumb() {
      if (!this._viewport || !this._track || !this._thumb) return;

      const { scrollWidth, clientWidth, scrollLeft } = this._viewport;
      const trackWidth = this._track.clientWidth;
      if (trackWidth <= 0 || scrollWidth <= clientWidth) return;

      const thumbWidth = Math.max(MIN_THUMB_PX, (clientWidth / scrollWidth) * trackWidth);
      const maxScroll = scrollWidth - clientWidth;
      const maxThumbTravel = trackWidth - thumbWidth;
      const thumbLeft = maxScroll > 0 ? (scrollLeft / maxScroll) * maxThumbTravel : 0;

      this._thumb.style.width = `${thumbWidth}px`;
      this._thumb.style.transform = `translateX(${thumbLeft}px)`;
    }

    _scrollFromThumbClientX(clientX) {
      const trackRect = this._track.getBoundingClientRect();
      const trackWidth = this._track.clientWidth;
      const thumbWidth = this._thumb.offsetWidth;
      const maxThumbTravel = trackWidth - thumbWidth;
      const { scrollWidth, clientWidth } = this._viewport;
      const maxScroll = scrollWidth - clientWidth;

      let thumbLeft = clientX - trackRect.left - thumbWidth / 2;
      thumbLeft = Math.max(0, Math.min(maxThumbTravel, thumbLeft));

      const scrollLeft = maxThumbTravel > 0 ? (thumbLeft / maxThumbTravel) * maxScroll : 0;
      this._viewport.scrollLeft = scrollLeft;
    }

    _onTrackPointerDown = (e) => {
      if (e.target !== this._track || !this._isMobile() || !this._isScrollable()) return;
      e.preventDefault();
      this._showIndicator();
      this._scrollFromThumbClientX(e.clientX);
      this._scheduleHide();
    };

    _onThumbPointerDown = (e) => {
      if (!this._isMobile() || !this._isScrollable()) return;
      e.preventDefault();
      e.stopPropagation();

      this._dragging = true;
      this._clearHideTimer();
      this._showIndicator();
      this._dragStartX = e.clientX;
      this._dragStartScrollLeft = this._viewport.scrollLeft;
      this._thumb.setPointerCapture(e.pointerId);

      this._thumb.addEventListener("pointermove", this._onThumbPointerMove);
      this._thumb.addEventListener("pointerup", this._onThumbPointerUp);
      this._thumb.addEventListener("pointercancel", this._onThumbPointerUp);
    };

    _onThumbPointerMove = (e) => {
      if (!this._dragging) return;

      const trackWidth = this._track.clientWidth;
      const thumbWidth = this._thumb.offsetWidth;
      const maxThumbTravel = trackWidth - thumbWidth;
      const { scrollWidth, clientWidth } = this._viewport;
      const maxScroll = scrollWidth - clientWidth;

      const deltaX = e.clientX - this._dragStartX;
      const scrollDelta = maxThumbTravel > 0 ? (deltaX / maxThumbTravel) * maxScroll : 0;
      this._viewport.scrollLeft = this._dragStartScrollLeft + scrollDelta;
    };

    _onThumbPointerUp = (e) => {
      this._endDrag();
      try {
        this._thumb.releasePointerCapture(e.pointerId);
      } catch (_) {
        /* ignore */
      }
      this._scheduleHide();
    };

    _endDrag() {
      if (!this._dragging) return;
      this._dragging = false;
      this._thumb?.removeEventListener("pointermove", this._onThumbPointerMove);
      this._thumb?.removeEventListener("pointerup", this._onThumbPointerUp);
      this._thumb?.removeEventListener("pointercancel", this._onThumbPointerUp);
    }
  }

  if (!customElements.get("horizontal-scrollbar")) {
    customElements.define("horizontal-scrollbar", HorizontalScrollbar);
  }
})();
